// The `PayPalMessages` component integrates PayPal's "Pay Later" messaging into a React application.
// It dynamically loads the PayPal SDK and renders financing options based on the provided `price` and `currency`.
// The component ensures client-side execution using the `'use client'` directive and utilizes a `useRef` to render the
// PayPal message container. Styled with a simple text layout, it helps display installment or financing offers
//  on product pages.

'use client'; // Ensures this runs on the client side

import { useEffect, useRef } from 'react';
import { loadScript, PayPalNamespace } from '@paypal/paypal-js';

interface PayPalMessagesProps {
  price: number;
  currency: any;
}

const PayPalMessages: React.FC<PayPalMessagesProps> = ({ price, currency }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function initializePayPalMessages() {
      try {
        if (typeof window === undefined) {
          return;
        }
        const paypal: PayPalNamespace | null = await loadScript({
          clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID as string,
          components: 'messages',
        });

        if (paypal?.Messages && containerRef.current) {
          containerRef.current.innerHTML = '';
          paypal
            .Messages({
              amount: price,
              currency: currency,
              style: {
                layout: 'text',
                logo: {
                  type: 'primary',
                  position: 'top',
                },
                text: {
                  size: 12,
                },
              },
              placement: 'product',
            })
            .render(containerRef.current);
        }
      } catch (error) {
        console.error('Failed to load PayPal SDK:', error);
      }
    }
    initializePayPalMessages();
  }, [price, currency]);

  return (
    <div>
      <div ref={containerRef}></div>
    </div>
  );
};

export default PayPalMessages;
